import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Plus, Search, X, User } from "lucide-react";

/**
 * Reusable typeahead customer picker — server-side debounced search.
 * Works for any role: backend masks mobile for non-admin roles, but matches
 * against the full mobile column, so billing staff can type the real number
 * without ever seeing it back.
 *
 * Props:
 *  - value: selected customer object (or null)
 *  - onChange: (customer) => void  — called when staff picks a row
 *  - onClear?: () => void — optional clear button handler
 *  - onNewClick?: () => void — optional "+ New customer" button
 *  - placeholder?: string
 *  - autoFocus?: boolean
 *  - testIdPrefix?: string — for data-testid
 */
export default function CustomerSearchPicker({
  value,
  onChange,
  onClear,
  onNewClick,
  placeholder = "Search by name or mobile…",
  autoFocus = false,
  testIdPrefix = "cust",
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const boxRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (value) return; // a customer is picked — don't re-search
    const t = setTimeout(async () => {
      if (!q || q.length < 1) { setResults([]); return; }
      setLoading(true);
      try {
        const { data } = await api.get("/customers", { params: { q } });
        setResults(data.slice(0, 25));
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, value]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (c) => {
    onChange?.(c);
    setOpen(false);
    setQ("");
    setResults([]);
  };

  const clear = () => {
    onClear?.();
    setQ("");
    setActiveIdx(0);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(results.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx(i => Math.max(0, i - 1)); }
    else if (e.key === "Enter" && results[activeIdx]) { e.preventDefault(); pick(results[activeIdx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  if (value) {
    return (
      <div className="flex items-center gap-2 border border-border rounded-md px-3 py-2 bg-muted/40" data-testid={`${testIdPrefix}-picked`}>
        <User className="w-4 h-4 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm truncate">{value.name}</div>
          <div className="text-xs text-muted-foreground font-mono">{value.mobile_masked || value.mobile}</div>
        </div>
        <Button variant="ghost" size="icon" type="button" onClick={clear} data-testid={`${testIdPrefix}-clear`}>
          <X className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  return (
    <div ref={boxRef} className="relative">
      <div className="flex items-center gap-1">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            ref={inputRef}
            value={q}
            placeholder={placeholder}
            autoFocus={autoFocus}
            onChange={e => { setQ(e.target.value); setOpen(true); setActiveIdx(0); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            className="pl-8"
            data-testid={`${testIdPrefix}-search`}
            autoComplete="off"
            inputMode="text"
          />
        </div>
        {onNewClick && (
          <Button variant="outline" size="icon" type="button" onClick={onNewClick} data-testid={`${testIdPrefix}-new`}>
            <Plus className="w-4 h-4" />
          </Button>
        )}
      </div>

      {open && q.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-md shadow-lg max-h-72 overflow-y-auto" data-testid={`${testIdPrefix}-dropdown`}>
          {loading && <div className="p-3 text-xs text-muted-foreground">Searching…</div>}
          {!loading && results.length === 0 && (
            <div className="p-3 text-sm text-muted-foreground">No matches.</div>
          )}
          {!loading && results.map((c, i) => (
            <button
              type="button"
              key={c.id}
              onMouseEnter={() => setActiveIdx(i)}
              onClick={() => pick(c)}
              className={`w-full text-left px-3 py-2 hover:bg-muted text-sm flex items-center gap-2 ${i === activeIdx ? "bg-muted" : ""}`}
              data-testid={`${testIdPrefix}-result-${c.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{c.name}</div>
                <div className="text-xs text-muted-foreground font-mono">{c.mobile_masked || c.mobile}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
